import { useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { useRouter } from 'next/router'
import { LoadingCover } from '../../../components/commons/LoadingCover'
import { getWpNewPostsByCategory } from '../../../lib/service'
import { getWpNewHouseKeeping, setWpNewHouseKeeping } from '../../../service/wp-blogs-store'
import { PartTimeHelperBooking } from '../../../components/index/service-category/domestic-service/PartTimeHelperBooking'
import { RequestProvider } from '../../../context/RequestContext'
import { Services } from '../../../components/index/service/Services'
import { AnimatePresence } from 'framer-motion'
import { SystemError } from '../../../components/error/SystemError'
import { openModal } from '../../../service/modal-service'
import { HelperServiceScope } from '../../../components/index/service-category/domestic-service/HelperServiceScope'
import { helperCostCalculation } from '../../../js/part-time-helper-cost'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-25'
  
export async function getStaticProps() {
    const countryName = "Singapore"
    const currencyCd = "SGD"
    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);
    const houseKeepingBlogs = blogsMaster['house-keeping'].blogArray;
    const wpNewPosts = getWpNewHouseKeeping().length > 0 ?  getWpNewHouseKeeping() : await getWpNewPostsByCategory(6, "house-keeping");
    setWpNewHouseKeeping(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Domestic Services")

    let helperService = []
    let babySitterService = []
    let elderlyCareService = []
    let cookingAssistanceService = []  

    helperCostCalculation(currencyCd, helperService, babySitterService, elderlyCareService, cookingAssistanceService)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        houseKeepingBlogs,
        wpNewPosts,
        countryName,
        currencyCd,
        helperService,
        elderlyCareService
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const houseKeepingBlogs = props.houseKeepingBlogs
    const wpNewPosts = props.wpNewPosts
    const requestCategorySettings = props.requestCategorySettings;
    const requestType = 'Domestic Services'
    const countryName = props.countryName
    const currencyCd = props.currencyCd
    const helperService = props.helperService
    const elderlyCareService = props.elderlyCareService
    
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ isLoading, setIsLoading ] = useState(false)
    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""})

    useEffect(() => {
        setRequestForm(router.query.requestForm)
    }, [router.query.requestForm])

    const priceTrendData = [
        { year: '2010', price: 10 },
        { year: '2015', price: 11 },
        { year: '2020', price: 15 },
        { year: '2025', price: 20 },
    ];

    const videoRef = useRef();

    useEffect(() => {    
        if(requestForm) return
        videoRef.current.play()
    }, [requestForm])

    const showJobScope = () =>{
        setErrorContent({'header': "Service Inclusions", 'isInfo': true, 'body': "Kitchen Assistance - Dishwashing, countertop wiping, and organizing for a tidy kitchen.\n\nHousekeeping - Sweeping/vacuuming, mopping, dusting, sanitizing, toilet cleaning, indoor side of window cleaning and maintaining overall home cleanliness.\n\nBabysitting, nanny & elderly care service.\n\nNote: The helper will not be carrying any equipment.\nThis service is for residential premises only."});
        openModal(setIsSystemError)
    }

  return (
    <>
      <Head>
        <title>Part Time Cleaners Singapore Rates | How Much Should I Pay?</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="According to our analytics research from March 26, 2023, the hourly rate for a part-time home cleaner in Singapore typically ranges from $18 to $30."/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/part-time-cleaner-singapore/part-time-cleaner-rates-singapore" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Part Time Cleaners Singapore Rates | How Much Should I Pay?`}/>
        <meta property="og:description" content="According to our analytics research from March 26, 2023, the hourly rate for a part-time home cleaner in Singapore typically ranges from $18 to $30." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-07T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/part-time-helper/part-time-helper-in-blue.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/part-time-helper/part-time-helper-in-blue.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/part-time-cleaner-singapore/part-time-cleaner-rates-singapore"/>
        <meta name="twitter:title" content={`Part Time Cleaners Singapore Rates | How Much Should I Pay?`}/> 
        <meta name="twitter:description" content="According to our analytics research from March 26, 2023, the hourly rate for a part-time home cleaner in Singapore typically ranges from $18 to $30."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/part-time-cleaner-singapore/part-time-cleaner-rates-singapore" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/part-time-cleaner-singapore/part-time-cleaner-rates-singapore"},"headline":"Part Time Cleaners Singapore Rates | How Much Should I Pay?","description":"According to our analytics research from March 26, 2023, the hourly rate for a part-time home cleaner in Singapore typically ranges from $18 to $30.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/part-time-helper/part-time-helper-in-blue.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-07T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"part-time-helper","item":"https://www.stringssg.com/sg/part-time-cleaner-singapore"},{"@type":"ListItem","position":2,"name":"rates"}]}]}`
          }}
        />
        </Head>
        <section id="part-time-cleaner-rates" className='relative'>
            <div className="-mt-14 relative w-full aspect-video sm:h-180 overflow-hidden">
                {/* Mobile Video */}
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover sm:hidden"
                    poster={s3Static + "blog/living/part-time-helper/part-time-cleaners-singapore-1920-1080.jpg"}
                    title="Part-time Helper Rates Singapore"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                >
                    <source src="/video/part-time-helper-web.mp4" />
                    <source src="/video/part-time-helper-web.mov" />
                </video>

                {/* Desktop Image */}
                <Image
                    src={s3Static + "part-time-helper/part-time-helper-in-blue.jpg"}
                    fill
                    className="hidden sm:block object-cover"
                    alt="Part-time cleaner rates in Singapore"
                    priority={false}
                    quality={100}
                    sizes="100vw"
                />
            </div>
            <RequestProvider>
                <div id="campaign" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2'>
                    <PartTimeHelperBooking requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} helperService={helperService} elderlyCareService={elderlyCareService}
                        setIsQuickBook={setIsQuickBook} countryName={countryName} currencyCd={currencyCd} helperType="Cleaner" setIsShowJobScope={setIsShowJobScope}/>
                </div>
                {requestForm &&
                    <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
                }      
            </RequestProvider>  
        </section>
        <div className='max-w-5xl mx-auto py-10 px-2 space-y-8'>
            <div className='space-y-2 text-center '>
                <h1 className='text-3xl md:text-4xl font-semibold'>Part Time Cleaner Rates Singapore</h1>
                <div className='text-gray-400 text-xs'>
                    <time dateTime={modifiedDate}>Updated - {new Date(modifiedDate).toUTCString().substring(0,16)}</time>
                </div>                    
            </div>
            <div className='leading-8 space-y-3'>
                <h2 className='text-lg md:text-xl font-semibold py-2'>How much does an hourly domestic maid cost in Singapore?</h2>
                <p>
                    According to our analytics research from March 26, 2023, the hourly rate for a part-time home cleaner in Singapore typically ranges from $18 to $30. 
                    This range can vary due to factors such as the service provider you choose, your location, and the specific services included.                        
                </p>
                <p>
                    Base on our statistics from over 7000 data points, majority of residents in Singapore pays somewhere around $20 to $26 per hour for hourly domestic helpers.
                </p>    
                <p>
                    At StringsSG, we pride ourselves on providing unbeatable value for part-time helper services. Our trusted and reliable service partners undergo rigorous 
                    background verifications before joining our platform. Additionally, our pricing becomes significantly more cost-effective when you request maids for 
                    longer hours due to attractive account discounts.
                </p>
            </div>
        </div>
        <div className='py-10 px-2 max-w-5xl mx-auto space-y-5'>
            <h2 className='font-bold text-center text-2xl uppercase'>Factors influencing part time maid hourly rates</h2>
            <div className='space-y-2 leading-8'>
                <h3><strong>1. Type of cleaning service:</strong></h3>
                <p>
                    General cleaning services typically encompass basic cleaning tasks that are part of regular maintenance. These services often include dusting, vacuuming, 
                    mopping floors, cleaning surfaces, and basic bathroom and kitchen sanitation. The rates for these services may be more standardized and commonly priced.
                </p>
                <p>
                    On the other hand, specialized services such as deep cleaning or upholstery cleaning involve more intensive and detailed work. Deep cleaning goes beyond the 
                    surface and targets areas that might not receive regular attention, such as behind appliances, inside cabinets, or high-touch areas. Upholstery 
                    cleaning involves delicate materials and specialized techniques to clean furniture fabrics effectively. These specialized services often require more time, 
                    specific skills, and sometimes specialized equipment or cleaning solutions, which can result in higher service charges compared to general cleaning.
                </p>
            </div>
            <div className='space-y-2 leading-8'>
                <h3><strong>2. Cleaning equipments:</strong></h3>
                <p>
                    If you do not have general home cleaning equipments for activities like sweeping, mopping, toilet brush etc, the part-time cleaners may charge extra cost 
                    to carry the equipments.
                </p>
                <p>
                    Furthermore, tasks that require advanced home cleaning equipment, like carpet shampooing or steam cleaning, can also affect the pricing. Such equipment may not be needed 
                    for routine cleaning but are essential for thorough deep cleaning and might incur additional costs due to their maintenance and usage.
                </p>
            </div>
            <div className='space-y-2 leading-8'>
                <h3><strong>3. Your location:</strong></h3>
                <p>
                    If you live in a less accessible area, extra charges may apply to accommodate the transportation cost of the cleaners.
                </p>
            </div>
            <div className='space-y-2 leading-8'>
                <h3><strong>4. The cleaning company brand:</strong></h3>
                <p>
                    Veteran companies that have build a strong brand tend to charge higher than newer companies. Newer cleaning companies do not have a strong online presence,
                    so they usually try to attract clients with paid advertisement, discounts and coupons.
                </p>
                <p>
                    It is also seen that newer companies who rely heavily on paid advertisement on search engines tend to charge very high to incur for their
                    ad expenses.
                </p>
            </div>
            <div className='space-y-2 leading-8'>
                <h3><strong>5. Goods & Services Tax (GST) registered companies:</strong></h3>
                <p>
                    If the cleaner provider company is GST registered in Singapore, they need to collect 9% tax on every sale. So, you need to pay for this tax
                    as well, which increases the total cost of cleaning service. When you get a quote for part-time home cleaners, make sure to check if GST is included or not.
                </p>
            </div>
        </div>
        <div className='py-10 px-2 max-w-5xl mx-auto space-y-5'>
            <h2 className='font-bold text-center text-2xl uppercase'>Part Time Helper Rates Trend</h2>
            <div className='space-y-2 leading-8'>
                <p>                        
                    Before 2011, part-time helper services did not garner much popularity in Singapore. The common practice among Singapore residents was to employ full-time 
                    live-in maids to manage their household chores. However, a shift began around 2015 when the demand for part-time helper services increased. This surge in 
                    demand coincided with the inadequacy of newer condominium sizes to accommodate full-time live-in maids.
                </p>
                <div className='h-64 max-w-lg mx-auto pt-5 text-center'>
                    <h3 className='text-sm font-bold '>Part-time Helper Hourly Rates Trend</h3>
                    <ResponsiveContainer width='100%' height='100%'>
                        <LineChart data={priceTrendData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='year' />
                        <YAxis />
                        <Tooltip formatter={(v) => [`S$${v}`, 'Price']} />
                        <Legend />
                        <Line type='monotone' dataKey='price' stroke='#ff9933' name='Price per Unit' strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>  
            </div>
        </div>
        <div className='my-10 py-6 px-2 max-w-5xl shadow rounded-lg mx-auto text-sm leading-8'>
            <h2 className='text-2xl md:text-3xl text-center my-5'>
                <strong>How to get the cheapest part-time cleaners in Singapore?</strong>
            </h2>
            <p className='py-3'>
                Although prioritizing quality and reliability remains crucial while seeking domestic maids, there are methods to discover more economical alternatives. 
                To identify budget-friendly part-time helpers in Singapore, consider these suggestions:
            </p>
            <div className='py-4 grid grid-cols-1 md:grid-cols-2 space-y-2'>
                <div className='space-y-2 px-2'>
                    <p>
                        1. Compare prices - Reach out to several part-time cleaners or companies and compare their service charges. This process can be quite laborious and time-consuming, and there is no 
                        assurance of finding a cheaper option.
                    </p>
                    <p>                
                        2. Promotions - Numerous part-time home cleaning companies provide promotions and discounts, especially during quieter periods. Explore their websites or social media profiles to stay updated on ongoing promotions.                        
                    </p> 
                    <p>
                        3. Packaged Service - Regular cleaning contracts might offer discounted rates compared to one-time services.
                    </p>
                    <p>
                        4. Negotiate - If you have a budget in mind, don&apos;t be afraid to negotiate. Not everyone has the skills though.
                    </p>
                    <p>
                        5. DIY - If you have the time, do the home cleaning at least once a week all by yourself, which can save you money in the long run.
                    </p>
                </div>
                <div className='relative w-full h-120'>
                    <Image className='rounded-lg'
                        src={s3Static + "blog/living/girl-saved-money-on-service.png"}
                        fill
                        style={{objectFit: 'cover'}}
                        placeholder='blur'
                        blurDataURL={s3Static + "placeholder.png"}
                        alt='Get affordable helpers for home cleaning'
                    /> 
                </div>
            </div>
            <div className='space-y-2 px-2'>
                <h3 className='font-bold text-lg'>StringsSG part-time helper service is the smartest way to get affordable home cleaning done!</h3>
                <p>Our platform consolidates over 7000 data points since May 2021 to standardize part-time helper rates for general home cleaning in Singapore.</p>
                <p>
                    We always have exciting discounts or promotions to help you get affordable home cleaning.
                </p>
            </div>
        </div>
        <div className='py-10 text-center'>
            <Link href="/sg/part-time-cleaner-singapore" className='px-3 py-3 border border-amber-600 text-orange-600 rounded-lg'>
                Hire Part-time Cleaner
            </Link>
        </div>                    
        <BlogsList blogs={houseKeepingBlogs} wpPosts={[]} wpNewPosts={wpNewPosts}/>
        {isShowJobScope &&
            <AnimatePresence>
                <HelperServiceScope/>
            </AnimatePresence>
        }
        {isLoading &&
          <LoadingCover/>
        }
        <AnimatePresence>
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        </AnimatePresence>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
